import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  Users, FileText, DollarSign, TrendingUp, Car, Home, 
  CheckCircle, Clock, XCircle, Eye, Search, Filter,
  ChevronDown, ChevronRight, RefreshCw, Download, Mail,
  AlertCircle, ArrowLeft, Building, CreditCard
} from 'lucide-react';
import '../styles/jp-morgan.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BethFinanceAdmin = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [consumers, setConsumers] = useState([]);
  const [applications, setApplications] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterLoanType, setFilterLoanType] = useState('all');
  const [selectedConsumer, setSelectedConsumer] = useState(null);
  const [selectedApplication, setSelectedApplication] = useState(null);

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [token]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch BETH Finance stats
      const statsRes = await axios.get(`${API}/admin/beth-finance/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(statsRes.data);

      // Fetch all consumers
      const consumersRes = await axios.get(`${API}/admin/beth-finance/consumers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConsumers(consumersRes.data.consumers || []);

      // Fetch all applications
      const appsRes = await axios.get(`${API}/admin/beth-finance/applications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApplications(appsRes.data.applications || []);
    } catch (err) {
      console.error('Error fetching BETH Finance data:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateApplicationStatus = async (applicationId, newStatus) => {
    try {
      await axios.put(`${API}/admin/beth-finance/applications/${applicationId}/status`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchData();
    } catch (err) {
      console.error('Error updating application:', err);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'processing': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'pending': case 'pending_payment': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'rejected': case 'denied': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'submitted': return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const filteredApplications = applications.filter(app => {
    const matchesSearch = 
      app.consumer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.consumer_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.application_id?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || app.status?.toLowerCase() === filterStatus;
    const matchesLoanType = filterLoanType === 'all' || app.loan_type === filterLoanType;
    return matchesSearch && matchesStatus && matchesLoanType;
  });

  const filteredConsumers = consumers.filter(consumer => {
    return consumer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
           consumer.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
           consumer.last_name?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(180deg, #0a1628 0%, #050d18 100%)' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#b8860b]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #0a1628 0%, #050d18 100%)' }}>
      {/* Header */}
      <header className="border-b" style={{ borderColor: 'rgba(184, 134, 11, 0.1)', background: '#0a1628' }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin-dashboard" className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Dashboard</span>
            </Link>
            <div className="h-6 w-px bg-gray-700"></div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#E31837] flex items-center justify-center">
                <span className="text-white font-bold text-xl">B</span>
              </div>
              <div>
                <span className="text-white font-bold text-lg">BETH Finance</span>
                <p className="text-[#E31837] text-xs">Admin Console</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button 
              onClick={fetchData}
              className="p-2 rounded-lg bg-[#1a2d4a] hover:bg-[#2a3d5a] text-gray-400 hover:text-white transition-all"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
            <div className="text-right">
              <p className="text-white text-sm font-medium">{user?.email}</p>
              <p className="text-gray-500 text-xs">Administrator</p>
            </div>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="border-b" style={{ borderColor: 'rgba(184, 134, 11, 0.1)', background: '#0d1d35' }}>
        <div className="max-w-7xl mx-auto px-6">
          <nav className="flex space-x-8">
            {[
              { id: 'overview', label: 'Overview', icon: TrendingUp },
              { id: 'applications', label: 'Applications', icon: FileText },
              { id: 'consumers', label: 'Consumers', icon: Users },
              { id: 'revenue', label: 'Revenue', icon: DollarSign }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                  activeTab === tab.id 
                    ? 'border-[#E31837] text-white' 
                    : 'border-transparent text-gray-500 hover:text-gray-300'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="rounded-xl p-6" style={{ background: '#0d1d35', border: '1px solid rgba(184, 134, 11, 0.1)' }}>
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-[#E31837]/20 flex items-center justify-center">
                    <Users className="w-6 h-6 text-[#E31837]" />
                  </div>
                  <span className="text-green-400 text-sm">+{stats?.new_consumers_today || 0} today</span>
                </div>
                <p className="text-3xl font-bold text-white">{stats?.total_consumers || consumers.length}</p>
                <p className="text-gray-500 text-sm">Total Consumers</p>
              </div>

              <div className="rounded-xl p-6" style={{ background: '#0d1d35', border: '1px solid rgba(184, 134, 11, 0.1)' }}>
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                    <FileText className="w-6 h-6 text-blue-400" />
                  </div>
                  <span className="text-blue-400 text-sm">{stats?.pending_applications || 0} pending</span>
                </div>
                <p className="text-3xl font-bold text-white">{stats?.total_applications || applications.length}</p>
                <p className="text-gray-500 text-sm">Total Applications</p>
              </div>

              <div className="rounded-xl p-6" style={{ background: '#0d1d35', border: '1px solid rgba(184, 134, 11, 0.1)' }}>
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-green-400" />
                  </div>
                </div>
                <p className="text-3xl font-bold text-white">{formatCurrency(stats?.total_revenue || 0)}</p>
                <p className="text-gray-500 text-sm">Total Revenue</p>
              </div>

              <div className="rounded-xl p-6" style={{ background: '#0d1d35', border: '1px solid rgba(184, 134, 11, 0.1)' }}>
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-purple-400" />
                  </div>
                </div>
                <p className="text-3xl font-bold text-white">{stats?.approval_rate || '0'}%</p>
                <p className="text-gray-500 text-sm">Approval Rate</p>
              </div>
            </div>

            {/* Loan Type Breakdown */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="rounded-xl p-6" style={{ background: '#0d1d35', border: '1px solid rgba(184, 134, 11, 0.1)' }}>
                <div className="flex items-center gap-3 mb-4">
                  <Car className="w-6 h-6 text-blue-400" />
                  <h3 className="text-lg font-bold text-white">Auto Loans</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Applications</span>
                    <span className="text-white font-medium">{stats?.auto_applications || applications.filter(a => a.loan_type === 'auto').length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Fee Revenue</span>
                    <span className="text-white font-medium">{formatCurrency((stats?.auto_applications || applications.filter(a => a.loan_type === 'auto').length) * 49)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Avg. Loan Amount</span>
                    <span className="text-white font-medium">{formatCurrency(stats?.avg_auto_loan || 25000)}</span>
                  </div>
                </div>
              </div>

              <div className="rounded-xl p-6" style={{ background: '#0d1d35', border: '1px solid rgba(184, 134, 11, 0.1)' }}>
                <div className="flex items-center gap-3 mb-4">
                  <Home className="w-6 h-6 text-purple-400" />
                  <h3 className="text-lg font-bold text-white">Mortgage Loans</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Applications</span>
                    <span className="text-white font-medium">{stats?.mortgage_applications || applications.filter(a => a.loan_type === 'mortgage').length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Fee Revenue</span>
                    <span className="text-white font-medium">{formatCurrency((stats?.mortgage_applications || applications.filter(a => a.loan_type === 'mortgage').length) * 199)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Avg. Loan Amount</span>
                    <span className="text-white font-medium">{formatCurrency(stats?.avg_mortgage_loan || 350000)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Applications */}
            <div className="rounded-xl" style={{ background: '#0d1d35', border: '1px solid rgba(184, 134, 11, 0.1)' }}>
              <div className="p-6 border-b" style={{ borderColor: 'rgba(184, 134, 11, 0.1)' }}>
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-bold text-white">Recent Applications</h3>
                  <button 
                    onClick={() => setActiveTab('applications')}
                    className="text-[#E31837] text-sm hover:underline"
                  >
                    View All
                  </button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-gray-500 text-xs uppercase">
                      <th className="px-6 py-3">Application</th>
                      <th className="px-6 py-3">Consumer</th>
                      <th className="px-6 py-3">Type</th>
                      <th className="px-6 py-3">Amount</th>
                      <th className="px-6 py-3">Status</th>
                      <th className="px-6 py-3">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y" style={{ borderColor: 'rgba(184, 134, 11, 0.1)' }}>
                    {applications.slice(0, 5).map((app, idx) => (
                      <tr key={idx} className="hover:bg-[#1a2d4a]/50 transition-colors">
                        <td className="px-6 py-4">
                          <span className="text-white font-mono text-sm">{app.application_id}</span>
                        </td>
                        <td className="px-6 py-4">
                          <div>
                            <p className="text-white text-sm">{app.consumer_name}</p>
                            <p className="text-gray-500 text-xs">{app.consumer_email}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            {app.loan_type === 'auto' ? (
                              <Car className="w-4 h-4 text-blue-400" />
                            ) : (
                              <Home className="w-4 h-4 text-purple-400" />
                            )}
                            <span className="text-gray-300 capitalize">{app.loan_type}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-white">{formatCurrency(app.loan_amount)}</td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded-full text-xs border ${getStatusColor(app.status)}`}>
                            {app.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-gray-400 text-sm">{formatDate(app.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* Applications Tab */}
        {activeTab === 'applications' && (
          <div className="rounded-xl" style={{ background: '#0d1d35', border: '1px solid rgba(184, 134, 11, 0.1)' }}>
            {/* Filters */}
            <div className="p-6 border-b" style={{ borderColor: 'rgba(184, 134, 11, 0.1)' }}>
              <div className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <input
                      type="text"
                      placeholder="Search by name, email, or ID..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 rounded-lg bg-[#1a2d4a] border border-[#2a3d5a] text-white placeholder:text-gray-500 focus:outline-none focus:border-[#E31837]"
                    />
                  </div>
                </div>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-4 py-2 rounded-lg bg-[#1a2d4a] border border-[#2a3d5a] text-white focus:outline-none"
                >
                  <option value="all">All Status</option>
                  <option value="pending_payment">Pending Payment</option>
                  <option value="processing">Processing</option>
                  <option value="submitted">Submitted</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
                <select
                  value={filterLoanType}
                  onChange={(e) => setFilterLoanType(e.target.value)}
                  className="px-4 py-2 rounded-lg bg-[#1a2d4a] border border-[#2a3d5a] text-white focus:outline-none"
                >
                  <option value="all">All Types</option>
                  <option value="auto">Auto Loan</option>
                  <option value="mortgage">Mortgage</option>
                </select>
              </div>
            </div>

            {/* Applications Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-gray-500 text-xs uppercase">
                    <th className="px-6 py-3">Application ID</th>
                    <th className="px-6 py-3">Consumer</th>
                    <th className="px-6 py-3">Type</th>
                    <th className="px-6 py-3">Loan Amount</th>
                    <th className="px-6 py-3">Income</th>
                    <th className="px-6 py-3">Status</th>
                    <th className="px-6 py-3">Date</th>
                    <th className="px-6 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y" style={{ borderColor: 'rgba(184, 134, 11, 0.1)' }}>
                  {filteredApplications.map((app, idx) => (
                    <tr key={idx} className="hover:bg-[#1a2d4a]/50 transition-colors">
                      <td className="px-6 py-4">
                        <span className="text-white font-mono text-sm">{app.application_id}</span>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-white text-sm">{app.consumer_name}</p>
                          <p className="text-gray-500 text-xs">{app.consumer_email}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          {app.loan_type === 'auto' ? (
                            <Car className="w-4 h-4 text-blue-400" />
                          ) : (
                            <Home className="w-4 h-4 text-purple-400" />
                          )}
                          <span className="text-gray-300 capitalize">{app.loan_type}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-white">{formatCurrency(app.loan_amount)}</td>
                      <td className="px-6 py-4 text-gray-300">{formatCurrency(app.annual_income)}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs border ${getStatusColor(app.status)}`}>
                          {app.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-400 text-sm">{formatDate(app.created_at)}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setSelectedApplication(app)}
                            className="p-1.5 rounded bg-[#1a2d4a] hover:bg-[#2a3d5a] text-gray-400 hover:text-white transition-all"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {app.status === 'processing' && (
                            <>
                              <button
                                onClick={() => updateApplicationStatus(app.application_id, 'approved')}
                                className="p-1.5 rounded bg-green-500/20 hover:bg-green-500/30 text-green-400 transition-all"
                                title="Approve"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => updateApplicationStatus(app.application_id, 'rejected')}
                                className="p-1.5 rounded bg-red-500/20 hover:bg-red-500/30 text-red-400 transition-all"
                                title="Reject"
                              >
                                <XCircle className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredApplications.length === 0 && (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400">No applications found</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Consumers Tab */}
        {activeTab === 'consumers' && (
          <div className="rounded-xl" style={{ background: '#0d1d35', border: '1px solid rgba(184, 134, 11, 0.1)' }}>
            {/* Search */}
            <div className="p-6 border-b" style={{ borderColor: 'rgba(184, 134, 11, 0.1)' }}>
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="text"
                  placeholder="Search consumers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 rounded-lg bg-[#1a2d4a] border border-[#2a3d5a] text-white placeholder:text-gray-500 focus:outline-none focus:border-[#E31837]"
                />
              </div>
            </div>

            {/* Consumers Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-gray-500 text-xs uppercase">
                    <th className="px-6 py-3">Consumer</th>
                    <th className="px-6 py-3">Contact</th>
                    <th className="px-6 py-3">Location</th>
                    <th className="px-6 py-3">Loan Type</th>
                    <th className="px-6 py-3">Status</th>
                    <th className="px-6 py-3">Joined</th>
                    <th className="px-6 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y" style={{ borderColor: 'rgba(184, 134, 11, 0.1)' }}>
                  {filteredConsumers.map((consumer, idx) => (
                    <tr key={idx} className="hover:bg-[#1a2d4a]/50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-[#E31837]/20 flex items-center justify-center">
                            <span className="text-[#E31837] font-bold">
                              {consumer.first_name?.[0]}{consumer.last_name?.[0]}
                            </span>
                          </div>
                          <div>
                            <p className="text-white font-medium">{consumer.first_name} {consumer.last_name}</p>
                            <p className="text-gray-500 text-xs">{consumer.consumer_id?.slice(0, 8)}...</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-gray-300 text-sm">{consumer.email}</p>
                          <p className="text-gray-500 text-xs">{consumer.phone}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-gray-300 text-sm">{consumer.city}, {consumer.state}</p>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          {consumer.loan_type === 'auto' ? (
                            <Car className="w-4 h-4 text-blue-400" />
                          ) : (
                            <Home className="w-4 h-4 text-purple-400" />
                          )}
                          <span className="text-gray-300 capitalize">{consumer.loan_type}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs border ${
                          consumer.status === 'active' 
                            ? 'bg-green-500/20 text-green-400 border-green-500/30' 
                            : 'bg-red-500/20 text-red-400 border-red-500/30'
                        }`}>
                          {consumer.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-400 text-sm">{formatDate(consumer.created_at)}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setSelectedConsumer(consumer)}
                            className="p-1.5 rounded bg-[#1a2d4a] hover:bg-[#2a3d5a] text-gray-400 hover:text-white transition-all"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <a
                            href={`mailto:${consumer.email}`}
                            className="p-1.5 rounded bg-[#1a2d4a] hover:bg-[#2a3d5a] text-gray-400 hover:text-white transition-all"
                            title="Send Email"
                          >
                            <Mail className="w-4 h-4" />
                          </a>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredConsumers.length === 0 && (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400">No consumers found</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Revenue Tab */}
        {activeTab === 'revenue' && (
          <div className="space-y-6">
            {/* Revenue Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="rounded-xl p-6" style={{ background: '#0d1d35', border: '1px solid rgba(184, 134, 11, 0.1)' }}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-green-400" />
                  </div>
                  <span className="text-gray-400">Total Revenue</span>
                </div>
                <p className="text-3xl font-bold text-white">
                  {formatCurrency(
                    applications.filter(a => a.payment_status === 'completed').reduce((sum, a) => sum + (a.fee_amount || 0), 0)
                  )}
                </p>
              </div>

              <div className="rounded-xl p-6" style={{ background: '#0d1d35', border: '1px solid rgba(184, 134, 11, 0.1)' }}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                    <Car className="w-5 h-5 text-blue-400" />
                  </div>
                  <span className="text-gray-400">Auto Loan Fees</span>
                </div>
                <p className="text-3xl font-bold text-white">
                  {formatCurrency(applications.filter(a => a.loan_type === 'auto' && a.payment_status === 'completed').length * 49)}
                </p>
                <p className="text-gray-500 text-sm mt-1">{applications.filter(a => a.loan_type === 'auto' && a.payment_status === 'completed').length} applications × $49</p>
              </div>

              <div className="rounded-xl p-6" style={{ background: '#0d1d35', border: '1px solid rgba(184, 134, 11, 0.1)' }}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                    <Home className="w-5 h-5 text-purple-400" />
                  </div>
                  <span className="text-gray-400">Mortgage Fees</span>
                </div>
                <p className="text-3xl font-bold text-white">
                  {formatCurrency(applications.filter(a => a.loan_type === 'mortgage' && a.payment_status === 'completed').length * 199)}
                </p>
                <p className="text-gray-500 text-sm mt-1">{applications.filter(a => a.loan_type === 'mortgage' && a.payment_status === 'completed').length} applications × $199</p>
              </div>
            </div>

            {/* Pending Payments */}
            <div className="rounded-xl" style={{ background: '#0d1d35', border: '1px solid rgba(184, 134, 11, 0.1)' }}>
              <div className="p-6 border-b" style={{ borderColor: 'rgba(184, 134, 11, 0.1)' }}>
                <h3 className="text-lg font-bold text-white">Pending Payments</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-gray-500 text-xs uppercase">
                      <th className="px-6 py-3">Application</th>
                      <th className="px-6 py-3">Consumer</th>
                      <th className="px-6 py-3">Type</th>
                      <th className="px-6 py-3">Fee</th>
                      <th className="px-6 py-3">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y" style={{ borderColor: 'rgba(184, 134, 11, 0.1)' }}>
                    {applications.filter(a => a.payment_status === 'pending' || a.status === 'pending_payment').map((app, idx) => (
                      <tr key={idx} className="hover:bg-[#1a2d4a]/50 transition-colors">
                        <td className="px-6 py-4 text-white font-mono text-sm">{app.application_id}</td>
                        <td className="px-6 py-4 text-gray-300">{app.consumer_name}</td>
                        <td className="px-6 py-4 text-gray-300 capitalize">{app.loan_type}</td>
                        <td className="px-6 py-4 text-yellow-400">{formatCurrency(app.fee_amount || (app.loan_type === 'auto' ? 49 : 199))}</td>
                        <td className="px-6 py-4 text-gray-400 text-sm">{formatDate(app.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {applications.filter(a => a.payment_status === 'pending' || a.status === 'pending_payment').length === 0 && (
                  <div className="text-center py-12">
                    <CheckCircle className="w-12 h-12 text-green-500/50 mx-auto mb-4" />
                    <p className="text-gray-400">No pending payments</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Application Detail Modal */}
      {selectedApplication && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0d1d35] rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-[#1a2d4a]">
            <div className="p-6 border-b border-[#1a2d4a] flex justify-between items-center">
              <h3 className="text-xl font-bold text-white">Application Details</h3>
              <button onClick={() => setSelectedApplication(null)} className="text-gray-400 hover:text-white">
                <XCircle className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-gray-500 text-sm">Application ID</p>
                  <p className="text-white font-mono">{selectedApplication.application_id}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-sm">Status</p>
                  <span className={`px-2 py-1 rounded-full text-xs border ${getStatusColor(selectedApplication.status)}`}>
                    {selectedApplication.status}
                  </span>
                </div>
                <div>
                  <p className="text-gray-500 text-sm">Consumer</p>
                  <p className="text-white">{selectedApplication.consumer_name}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-sm">Email</p>
                  <p className="text-white">{selectedApplication.consumer_email}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-sm">Loan Type</p>
                  <p className="text-white capitalize">{selectedApplication.loan_type}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-sm">Loan Amount</p>
                  <p className="text-white">{formatCurrency(selectedApplication.loan_amount)}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-sm">Loan Purpose</p>
                  <p className="text-white capitalize">{selectedApplication.loan_purpose?.replace('_', ' ')}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-sm">Term</p>
                  <p className="text-white">{selectedApplication.desired_term_months} months</p>
                </div>
                <div>
                  <p className="text-gray-500 text-sm">Employer</p>
                  <p className="text-white">{selectedApplication.employer_name}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-sm">Annual Income</p>
                  <p className="text-white">{formatCurrency(selectedApplication.annual_income)}</p>
                </div>
                {selectedApplication.loan_type === 'auto' && (
                  <div className="col-span-2">
                    <p className="text-gray-500 text-sm">Vehicle</p>
                    <p className="text-white">{selectedApplication.vehicle_year} {selectedApplication.vehicle_make} {selectedApplication.vehicle_model}</p>
                  </div>
                )}
                {selectedApplication.loan_type === 'mortgage' && (
                  <>
                    <div className="col-span-2">
                      <p className="text-gray-500 text-sm">Property Address</p>
                      <p className="text-white">{selectedApplication.property_address}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-sm">Property Value</p>
                      <p className="text-white">{formatCurrency(selectedApplication.property_value)}</p>
                    </div>
                  </>
                )}
              </div>

              {/* Action Buttons */}
              {selectedApplication.status === 'processing' && (
                <div className="flex gap-4 pt-4 border-t border-[#1a2d4a]">
                  <button
                    onClick={() => {
                      updateApplicationStatus(selectedApplication.application_id, 'approved');
                      setSelectedApplication(null);
                    }}
                    className="flex-1 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-all flex items-center justify-center gap-2"
                  >
                    <CheckCircle className="w-5 h-5" /> Approve
                  </button>
                  <button
                    onClick={() => {
                      updateApplicationStatus(selectedApplication.application_id, 'rejected');
                      setSelectedApplication(null);
                    }}
                    className="flex-1 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-all flex items-center justify-center gap-2"
                  >
                    <XCircle className="w-5 h-5" /> Reject
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Consumer Detail Modal */}
      {selectedConsumer && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0d1d35] rounded-xl w-full max-w-lg border border-[#1a2d4a]">
            <div className="p-6 border-b border-[#1a2d4a] flex justify-between items-center">
              <h3 className="text-xl font-bold text-white">Consumer Details</h3>
              <button onClick={() => setSelectedConsumer(null)} className="text-gray-400 hover:text-white">
                <XCircle className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-4 pb-4 border-b border-[#1a2d4a]">
                <div className="w-16 h-16 rounded-full bg-[#E31837]/20 flex items-center justify-center">
                  <span className="text-[#E31837] font-bold text-xl">
                    {selectedConsumer.first_name?.[0]}{selectedConsumer.last_name?.[0]}
                  </span>
                </div>
                <div>
                  <p className="text-xl font-bold text-white">{selectedConsumer.first_name} {selectedConsumer.last_name}</p>
                  <p className="text-gray-400">{selectedConsumer.email}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-gray-500 text-sm">Phone</p>
                  <p className="text-white">{selectedConsumer.phone}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-sm">Status</p>
                  <span className={`px-2 py-1 rounded-full text-xs border ${
                    selectedConsumer.status === 'active' 
                      ? 'bg-green-500/20 text-green-400 border-green-500/30' 
                      : 'bg-red-500/20 text-red-400 border-red-500/30'
                  }`}>
                    {selectedConsumer.status}
                  </span>
                </div>
                <div className="col-span-2">
                  <p className="text-gray-500 text-sm">Address</p>
                  <p className="text-white">{selectedConsumer.address}, {selectedConsumer.city}, {selectedConsumer.state} {selectedConsumer.zip_code}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-sm">Loan Type</p>
                  <p className="text-white capitalize">{selectedConsumer.loan_type}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-sm">Joined</p>
                  <p className="text-white">{formatDate(selectedConsumer.created_at)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BethFinanceAdmin;
